import { useEffect, useState } from 'react';
import type { NextPage } from 'next';
import { useRouter } from 'next/router';
import dayjs from 'dayjs';

import { Container, Icon } from '@dansk-metal/ui';

import { ErrorFrame } from '@web/components/error-frame/error-frame';
import HeroTitle from '@web/components/hero-block/hero-title/hero-title';
import { LinkButton } from '@web/components/link-button/link-button';
import { ThemedSkeleton } from '@web/components/themed-skeleton/themed-skeleton';
import { UserPaymentForm } from '@web/components/user-payment-form/user-payment-form';

import TemplateLayout from '@web/layouts/template/template';

import { useAuthenticatedCRMRequest, useAuthenticatedCRMRequestConfig } from '@web/services/crm-api/hooks';
import { getMemberContributions, GetMemberContributionsParams, getMemberPaymentForm, PaymentForm } from '@web/services/crm-api/rest';
import { crmError } from '@web/services/errorhandler/appError';
import { HeroAlignment } from '@web/services/umbraco/types/basic/Alignment';
import { BackgroundColor } from '@web/services/umbraco/types/basic/Color';
import { UmbracoErrorType } from '@web/services/umbraco/types/basic/ErrorTypes';
import { Link as UmbracoLink } from '@web/services/umbraco/types/basic/Link';
import { UmbracoPage } from '@web/services/umbraco/types/documents/UmbracoDocument';
import { LinkStyle } from '@web/services/umbraco/types/elements/Action';

import styles from './user-payment-info-page.module.scss';

export const userPaymentInfoPageContentAlias = 'userPaymentInfoPage' as const;

export type UserPaymentInfoPageData = UmbracoPage<
	typeof userPaymentInfoPageContentAlias,
	{
		title: string;
		subtitle?: string;
		profileUrl: UmbracoLink;
		membershipInfoUrl: UmbracoLink;
	}
>;


const UserPaymentInfoPageTemplate: NextPage<{ pageData: UserPaymentInfoPageData }> = ({ pageData }) => {
	const [shouldShowConfirmation, setShouldShowConfirmation] = useState(false);
	const { title, subtitle, profileUrl, membershipInfoUrl } = pageData.properties;
	const [paymentMethod, setPaymentMethod] = useState('' as string);
	const { query: { success, paymentMethod: successPaymentMethod } } = useRouter();
	const { memberId, requestConfig, isMember } = useAuthenticatedCRMRequestConfig();
	const currentDate = dayjs();

	const memberContributionsParams: GetMemberContributionsParams = {
		startDate: currentDate.startOf('month').set('hour', 0).format('YYYY-MM-DDTHH:mm'),
		endDate: currentDate.endOf('month').set('hour', 0).format('YYYY-MM-DDTHH:mm'),
	};

	const {
		data: memberPaymentForm,
		isLoading: isMemberPaymentFormLoading,
		error: paymentFormError,
	} = useAuthenticatedCRMRequest({
		request: () => getMemberPaymentForm(memberId, requestConfig),
	});

	const {
		data: memberContributions,
		isLoading: isContributionLoading,
		error: contributionError,
	} = useAuthenticatedCRMRequest({
		request: () => getMemberContributions(memberId, memberContributionsParams, requestConfig),
	});


	function getPaymentMethod(paymentOptions) {
		for (const [key, value] of Object.entries(paymentOptions)) {
			if (value) {
				if (key === PaymentForm.Pbs) {
					setPaymentMethod('betalingsservice');
				} else if (key === PaymentForm.MobilePay) {
					setPaymentMethod('MobilePay');
				} else if (key === PaymentForm.Card) {
					setPaymentMethod('betalningskort');
				}
			}
		}
	}

	function translatePaymentMethod(method) {
		if (method === PaymentForm.MobilePay) {
			return 'MobilePay';
		}

		if (method === PaymentForm.Card) {
			return 'betalningskort';
		}
	}

	const onSubmitSuccess = (paymentData) => {
		getPaymentMethod(paymentData.paymentOptions);
		window.scrollTo(0, 0);
		setShouldShowConfirmation(true);
	};

	useEffect(() => {
		if (success === 'true') {
			setShouldShowConfirmation(true);
		}
	}, [success]);

	const isLoading = isMemberPaymentFormLoading || isContributionLoading;

	if (!isMember) {
		return <ErrorFrame errorType={UmbracoErrorType.Forbidden} />;
	}

	if (paymentFormError || contributionError) {
		throw crmError({ cause: (paymentFormError || contributionError) as unknown as Error });
	}

	return (
		<TemplateLayout>
			{shouldShowConfirmation ? (
				<div className={styles.confirmation_container}>
					<div className={styles.content}>
						<div className={styles.header_container}>
							<Icon icon="checkmark-confirmation" size={72} />
							<div className={styles.text_container}>
								<h2>
									Din betalings&shy;metode er ændret til {paymentMethod || translatePaymentMethod(successPaymentMethod)}
								</h2>
								<p className={styles.body_text}>
									Bemærk - der kan gå nogle minutter, før ændringen træder i kraft. Du modtager en besked i din postkasse.
								</p>
							</div>
						</div>
						<LinkButton link={profileUrl} icon={'account'} type={LinkStyle.DarkBlueButton} />
					</div>
				</div>
			) : (
				<>
					{!!title && (
						<HeroTitle
							title={title}
							subtitle={subtitle}
							alignment={HeroAlignment.Left}
							background={BackgroundColor.White}
						/>
					)}
					<Container className={styles.container}>
						{isLoading && (
							<ThemedSkeleton className={styles.loader} containerClassName={styles.loader_wrapper} />
						)}
						{memberPaymentForm?.paymentForm && !isLoading && (
							<UserPaymentForm
								onSubmitSuccess={onSubmitSuccess}
								paymentMethod={memberPaymentForm.paymentForm}
								membershipInfoUrl={membershipInfoUrl}
								memberContributions={memberContributions}
							/>
						)}
					</Container>
				</>
			)}
		</TemplateLayout>
	);
};

export default UserPaymentInfoPageTemplate;
